import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PwaInstallButton } from './PwaInstall';
import { 
  LayoutDashboard, FileText, Calendar, BarChart3, 
  Mic, MessageSquare, Coins, Link2, Settings, LogOut, Flame, Shield, Globe, Wrench
} from 'lucide-react';

const Sidebar = ({ activeView, onViewChange, isFounder, mobileOpen }) => {
  const { user, subscription, logout } = useAuth();

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'articles', label: 'Articles', icon: FileText },
    { id: 'scheduled', label: 'Scheduled', icon: Calendar },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'voice', label: 'Voice AI', icon: Mic, premium: !subscription?.voice_enabled },
    { id: 'assistant', label: 'AI Assistant', icon: MessageSquare, premium: !subscription?.ai_assistant },
    { id: 'credits', label: 'Credits', icon: Coins },
    { id: 'integrations', label: 'Integrations', icon: Link2 },
    { id: 'tools', label: 'Content Tools', icon: Wrench },
    { id: 'brand', label: 'Brand Voice', icon: Globe },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (isFounder) {
    navItems.push({ id: 'admin', label: 'Admin', icon: Shield, admin: true });
  }

  return (
    <div className={`sidebar ${mobileOpen ? 'open' : ''}`} data-testid="sidebar">
      {/* Logo */}
      <div className="flex items-center gap-2.5">
        <img src="/stilos-logo.png" alt="Stilos" className="w-9 h-9 rounded-lg object-cover" />
        <span className="font-display font-semibold text-base tracking-tight">Stilos</span>
      </div>

      {/* User Info */}
      <div className="mt-5 p-3 rounded-xl bg-[var(--color-surface)]">
        <p className="font-medium text-sm truncate">{user?.full_name || user?.email}</p>
        <p className="text-xs text-[var(--color-muted)] capitalize mt-0.5">{subscription?.tier || 'Free'} plan</p>
      </div>

      {/* Credits & Streak */}
      <div className="mt-3 flex gap-2">
        <div className="credits-display flex-1 justify-center">
          <Coins className="w-3.5 h-3.5" />
          <span>{subscription?.credits_remaining?.toFixed(1) || 0}</span>
        </div>
        {subscription?.current_streak > 0 && (
          <div className="streak-display">
            <Flame className="w-3.5 h-3.5" />
            <span>{subscription?.current_streak}</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              className={`sidebar-item w-full ${activeView === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-[18px] h-[18px]" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.premium && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(232,115,92,0.08)] text-[var(--color-coral)] font-semibold">
                  Pro
                </span>
              )}
              {item.admin && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-surface)] text-[var(--color-muted)] font-semibold">
                  Admin
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Upgrade CTA */}
      {subscription?.tier === 'free' && (
        <div className="mt-auto p-4 rounded-2xl text-center bg-[var(--color-surface)]">
          <p className="text-sm font-medium mb-1">Unlock Voice AI</p>
          <p className="text-xs text-[var(--color-muted)] mb-3">
            Speak your ideas, get full articles
          </p>
          <button 
            onClick={() => onViewChange('credits')}
            className="btn btn-accent w-full text-sm"
          >
            Upgrade now
          </button>
        </div>
      )}

      {/* Install App */}
      <div className="px-3 mb-2">
        <PwaInstallButton />
      </div>

      {/* Logout */}
      <button
        onClick={logout}
        className="sidebar-item w-full mt-3"
        data-testid="logout-btn"
      >
        <LogOut className="w-[18px] h-[18px]" />
        <span>Sign out</span>
      </button>
    </div>
  );
};

export default Sidebar;
